import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import color from 'color';

import React, { useState, useEffect } from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';

import {
  Redirect,
} from 'react-router-dom';

import DefaultAdmin from 'hive-admin';
import {
  Provider as ClipboardProvider,
} from 'hive-admin/src/components/ClipboardContext';

import { Helmet } from 'react-helmet';

import theme from '../theme';

import { logoLoginSrc, logoSidebarSrc, faviconSrc } from './Logo';

const FullWidthFormFieldGlobalStyle = createGlobalStyle`
  #content > * > .ant-form > .ant-row > [data-full-width-form-field="true"] {
    width: calc(100vw - 380px);
    margin-left: min(0px, calc(calc(calc(100vw - 380px) - 820px) / -2));
    [data-expanded-sidebar="false"] & {
      width: calc(100vw - 180px);
      margin-left: min(0px, calc(calc(calc(100vw - 180px) - 820px) / -2));
    }
    [data-is-tablet="true"] & {
      width: calc(100vw - 80px);
      margin-left: min(0px, calc(calc(calc(100vw - 80px) - 820px) / -2));
    }
    [data-is-mobile="true"] & {
      width: calc(100vw - 20px);
      margin-left: min(0px, calc(calc(calc(100vw - 20px) - 820px) / -2));
    }
  }
`;

const getColorVariables = ({ primary, secondary, button }) => `
  --primary-color: ${primary};
  --secondary-color: ${secondary};

  --color-foreground: ${color(primary).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
    };

  --color-button: ${button};
  --color-button-text: ${color(button).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
    };

  --antd-color-primary: ${primary};
  --antd-color-secondary: ${secondary};

  --antd-color-foreground: ${color(primary).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
    };

  --antd-color-button: ${button};
  --antd-color-button-text: ${color(button).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
    };

  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-lighten-${index * 5}:
      ${color(primary).lighten(0.05 * index).hex()};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-darken-${index * 5}:
      ${color(primary).darken(0.05 * index).hex()};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-fade-${index * 5}:
      ${color(primary).alpha(0.05 * index)};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-tint-${index * 5}:
      ${color(primary).whiten(0.05 * index).hex()};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-shade-${index * 5}:
      ${color(primary).blacken(0.05 * index).hex()};
    `).join('\n')
  };

  --antd-color-primary-1: ${color(primary).lighten(0.6).hex()};
  --antd-color-primary-1: ${color(primary).alpha(0.1)};

  --antd-color-primary-2: ${color(primary).lighten(0.5).hex()};
  --antd-color-primary-3: ${color(primary).lighten(0.4).hex()};
  --antd-color-primary-4: ${color(primary).lighten(0.3).hex()};
  --antd-color-primary-5: ${color(primary).lighten(0.2).hex()};
  --antd-color-primary-6: ${color(primary).darken(0.2).hex()};
  --antd-color-primary-7: ${color(primary).darken(0.3).hex()};
  --antd-color-primary-8: ${color(primary).darken(0.4).hex()};
  --antd-color-primary-9: ${color(primary).darken(0.5).hex()};
  --antd-color-primary-10: ${color(primary).darken(0.6).hex()};
`;

const FaviconDelayed = ({ src }) => {
  const [favicon, setFavicon] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFavicon(faviconSrc);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  return (
    favicon ? (
      <Helmet>
        <link rel="icon" href={src} />
      </Helmet>
    ) : null
  );
};

export default class Admin extends DefaultAdmin {
  renderContent(extraProps = {}) {
    const { page, _searchParams } = this.props;
    if (!page) {
      return null;
    }
    const destination = page.getRedirect({ ...this.props, ...page });
    const state = { from: this.props.location };
    if (isObject(destination)) {
      return (
        <Redirect
          key="content"
          to={Object.assign(
            destination,
            { state: Object.assign(state, destination.state || {}) }
          )}
        />
      );
    }
    if (isString(destination)) {
      return (
        <Redirect
          key="content"
          to={{
            pathname: destination,
            state,
          }}
        />
      );
    }
    const { render, type, getSkip, skip, id, ...restConfig } = page;
    return (
      <ClipboardProvider storage={this.props.storage}>
        {
          page.render({
            ...this.props,
            key: page.path,
            ...restConfig,
            ...extraProps,
          })
        }
      </ClipboardProvider>
    );
  }

  renderAfterStyle() {
    return (
      <>
        <FullWidthFormFieldGlobalStyle />
        {super.render()}
      </>
    );
  }

  renderWithDefaultStyle({
    colorPrimary,
    colorSecondary,
    colorButton,
    colorTitle,
    logoSidebar,
    logoLogin,
    favicon,
  }) {
    return (
      <>
        <Helmet>
          <meta
            name="theme-color"
            content={colorPrimary}
          />
        </Helmet>

        <FaviconDelayed src={faviconSrc} />

        <style>
          {`
            :root {
              ${getColorVariables({
                primary: colorPrimary,
                secondary: colorSecondary || colorPrimary,
                button: colorButton || colorPrimary,
                title: colorTitle,
              })
            }
          `}
        </style>
        <ThemeProvider theme={{
          less: {
            ...theme.less,
            primaryColor: colorPrimary,
            secondaryColor: colorSecondary || colorPrimary,
            buttonColor: colorButton || colorPrimary,
            titleColor: colorTitle,
          },
          logoSidebar,
          logoLogin,
          favicon,
        }}
        >
          {this.renderAfterStyle()}
        </ThemeProvider>
      </>
    );
  }

  render() {
    const renderAdmin = ({
      defaultSystemColor = '#FF6961',
      defaultTitleColor = '#ffffff',
    } = {}) => (
      this.renderWithDefaultStyle({
        favicon: faviconSrc,
        logoLogin: logoLoginSrc,
        logoSidebar: logoSidebarSrc,
        colorPrimary: defaultSystemColor,
        colorSecondary: defaultSystemColor,
        colorButton: defaultSystemColor,
        colorTitle: defaultTitleColor,
      })
    );

    return renderAdmin();
  }
}
